'use client';

/** SDL-owned icon component mapping semantic names to visual implementations -- the ONLY way icons should be used in the application. */

import { clsx } from 'clsx';
import { colors } from '../css-refs.colors.ts';
import { spacing } from '../css-refs.spacing.ts';
import { semanticActions, iconSizes, INHERITED_ICON_COLOR } from './semantic-tokens.ts';
import { useKeyboardClick } from '../hooks/useKeyboardClick.ts';
import { getIcon } from './registry.tsx';
import type { IconColor, IconSize, SemanticIconProps } from '@federa/types/ui-icons';

/**
 * The semantic-action row an icon name resolves to, or `undefined` when the name has no row.
 *
 * Derived from `semanticActions.icons` rather than declared, so a token added to that record widens this type by
 * construction; the three readers below narrow it further with `'fill' in actionDef` / `'spin' in actionDef` because
 * the union's arms do NOT all carry those members.
 */
type SemanticActionDef = (typeof semanticActions.icons)[keyof typeof semanticActions.icons] | undefined;

const sizeMap: Record<IconSize, string> = {
	xxs: spacing[2],
	xs: iconSizes.xs,
	sm: iconSizes.sm,
	md: iconSizes.md,
	lg: iconSizes.lg,
	xl: iconSizes.xl,
	'2xl': iconSizes['2xl'],
	'3xl': iconSizes['3xl'],
	'4xl': iconSizes['4xl'],
	tooltip: iconSizes.tooltip,
	toast: iconSizes.toast,
	button: iconSizes.button,
	navigation: iconSizes.navigation,
	hero: iconSizes.hero,
};

const colorMap: Record<IconColor, string> = {
	primary: colors.brand.primary,
	secondary: colors.text.secondary,
	success: colors.semantic.success,
	warning: colors.semantic.warning,
	muted: colors.text.tertiary,
	inherit: INHERITED_ICON_COLOR,
	error: colors.semantic.error,
	brand: colors.brand.primary,
};

// Helper to determine if icon should be filled
function shouldIconBeFilled(state: SemanticIconProps['state'], actionDef: SemanticActionDef): boolean {
	if (state === 'active' || state === 'filled') return true;
	if (actionDef && 'fill' in actionDef) return true;
	return false;
}

// Helper to determine if icon should spin
function shouldIconSpin(spin: boolean, name: SemanticIconProps['name'], actionDef: SemanticActionDef): boolean {
	if (spin) return true;
	if (name === 'loading') return true;
	if (actionDef && 'spin' in actionDef) return true;
	return false;
}

function buildIconClasses(className: string, shouldSpin: boolean, isClickable: boolean): string {
	return clsx(
		className,
		shouldSpin && 'animate-spin',
		isClickable && 'cursor-pointer',
		isClickable && 'transition-opacity hover:opacity-80 active:opacity-60',
	);
}

function getAriaLabel(ariaLabel: string | undefined, actionDef: SemanticActionDef, name: string): string {
	if (ariaLabel) return ariaLabel;
	if (actionDef?.ariaLabel) return actionDef.ariaLabel;
	return name;
}

function useIconDerived(props: SemanticIconProps) {
	const { name, state = 'default', size = 'md', color = 'inherit', className = '', ariaLabel, spin = false, onClick } = props;

	const actionDef = semanticActions.icons[name];
	const isFilled = shouldIconBeFilled(state, actionDef);
	const iconSize = sizeMap[size];
	const iconColor = colorMap[color];
	const label = getAriaLabel(ariaLabel, actionDef, name);
	const iconShouldSpin = shouldIconSpin(spin, name, actionDef);
	const isClickable = Boolean(onClick);
	const classes = buildIconClasses(className, iconShouldSpin, isClickable);

	return { isFilled, iconSize, iconColor, label, classes, isClickable };
}

export function SemanticIcon(props: SemanticIconProps) {
	const { name, style, onClick, decorative = false } = props;
	const { isFilled, iconSize, iconColor, label, classes, isClickable } = useIconDerived(props);
	// A DECORATIVE ICON IS NOT A CONTROL (iss-e5e2ec24): hidden from assistive technology, it can carry no reachable
	// click, so the pair is refused loudly rather than rendered as a dead target.
	if (decorative && isClickable) throw new Error(`SemanticIcon "${name}" is decorative and carries onClick: a hidden icon cannot be a control`);

	const IconComponent = getIcon(name);
	const { handleKeyDown: keyboardHandler } = useKeyboardClick({
		onClick: () => onClick?.(),
		disabled: !onClick,
	});
	const handleKeyDown = isClickable ? keyboardHandler : undefined;

	const SvgIcon = IconComponent as React.ComponentType<React.SVGProps<SVGSVGElement>>;
	return (
		<SvgIcon
			style={{
				width: iconSize,
				height: iconSize,
				color: iconColor,
				fill: isFilled ? iconColor : 'none',
				...style,
			}}
			className={classes}
			// THE WORD IS SAID ONCE: a decorative glyph beside its own word writes no name and hides itself; every other
			// icon announces the token-table label (or the caller's) as before.
			{...(decorative ? { 'aria-hidden': true } : { 'aria-label': label })}
			onClick={onClick}
			onKeyDown={handleKeyDown}
			role={isClickable ? 'button' : undefined}
			tabIndex={isClickable ? 0 : undefined}
		/>
	);
}
